import { useCallback, useEffect, useState } from 'react';

export const COLLAPSE_QUERY = '(max-width: 1279px)';
export const PHONE_QUERY = '(max-width: 767px)';
const STORAGE_KEY = 'c2.sidebar.collapsed';

const readStored = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v == null ? null : v === '1';
  } catch (e) {
    return null;
  }
};

const writeStored = (collapsed) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  } catch (e) {
    /* private window / blocked storage: the choice just doesn't persist */
  }
};

/** Subscribe to a media query; React 16-safe (no MUI useMediaQuery). */
export const useMediaMatch = (query) => {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
};

/**
 * Sidebar frame state. Below 1280px the rail collapses by default; the user can override
 * (persisted). Below 768px the sidebar is an off-canvas drawer, so `collapsed` is moot and
 * `drawerOpen` drives it; the drawer closes on route change (pass `pathname`).
 */
export default function useSidebarState(pathname) {
  const narrow = useMediaMatch(COLLAPSE_QUERY);
  const phone = useMediaMatch(PHONE_QUERY);
  const [override, setOverride] = useState(readStored);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const collapsed = !phone && (override != null ? override : narrow);

  const toggle = useCallback(() => {
    setOverride((prev) => {
      const next = !(prev != null ? prev : narrow);
      writeStored(next);
      return next;
    });
  }, [narrow]);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return { collapsed, toggle, phone, drawerOpen, openDrawer, closeDrawer };
}
